'use client';

import CustomButton from "@/components/common/buttons/button";
import classes from "./providers_mosaic_view.module.css";
import ArrowRight from '@/assets/svg/icons/arrow-right.svg';
import Vectors from '@/assets/svg/other_providers_background.svg';
import SafeImage from "@/components/common/safe_image";
import customImageLoader from '../../../../imageLoader';
import EmptyProviderLogo from '@/assets/png/empty-event-logo.png';
import CustomSVG from "@/components/common/svg";
import MapPin from '@/assets/svg/icons/map-pin.svg';
import Heart from '@/assets/svg/icons/heart.svg';
import ArrowUpRight from '@/assets/svg/icons/arrow-up-right.svg';
import { Fragment, useContext, useEffect, useState } from "react";
import Link from "next/link";
import AuthContext from "@/providers/auth-context";
import { useSanitizedHTML } from "@/utils/sanitizeHTMLHooks";

const gcd = 37.5;
const MOSAIC_GRID_COLUMNS = 53;

// Grid line numbers are 1-based; span math mirrors the previous table's colSpan/rowSpan layout exactly.
function mosaicCell(colStart, colSpan, rowStart, rowSpan) {
    return { gridColumn: `${colStart} / span ${colSpan}`, gridRow: `${rowStart} / span ${rowSpan}` };
}

const MOSAIC_BANNER_SIZES = '(max-width: 940px) calc(100vw - 24px), calc(33.33vw - 56px)';
const MOSAIC_MOBILE_BANNER_SIZES = '(max-width: 600px) calc(100vw - 40px), calc((100vw - 64px) / 2)';
const MOSAIC_PROVIDER_CARD_SIZES = {
    width21: '(max-width: 940px) calc(100vw - 24px), calc(39.62vw - 80px)',
    width12: '(max-width: 940px) calc(100vw - 24px), calc(22.64vw - 56px)',
    width11: '(max-width: 940px) calc(100vw - 24px), calc(20.75vw - 53px)',
};
const MOSAIC_MOBILE_PROVIDER_SIZES = '(max-width: 600px) calc(100vw - 40px), calc((100vw - 64px) / 2)';
const ALL_MOBILE_TYPES_KEY = 'all';

function buildFallbackMobileTabs(providers = [], providerTypes = []) {
    const visibleTypes = providerTypes.filter(Boolean).slice(0, 8);
    const tabs = [{
        key: ALL_MOBILE_TYPES_KEY,
        name: 'Összes',
        providers: providers.filter(Boolean),
    }];

    visibleTypes.forEach((type) => {
        const key = `type-${type.id || type._url || type.name}`;
        const tabProviders = providers.filter((provider) => matchesProviderType(provider, type, visibleTypes));

        if (!tabProviders.length) return;

        tabs.push({
            key,
            name: type?.name || '-',
            type,
            providers: tabProviders,
        });
    });

    return tabs;
}

function stripHtml(value = "") {
    return String(value).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function hasRenderableBannerMarkup(value = '') {
    if (!value || typeof value !== 'string') return false;

    return /<[^>]+>/.test(value);
}

function getBannerImageValue(value) {
    if (!value) return '';

    if (typeof value === 'string') return value.trim();

    if (typeof value === 'object') {
        const objectImage = String(value.webp || value.file || value.url || value.src || '').trim();
        if (objectImage) return objectImage;
    }

    return '';
}

function extractImageFromMarkup(markup = '') {
    if (!markup || typeof markup !== 'string') return '';

    const srcMatch = markup.match(/<img[^>]+src=["']([^"']+)["']/i);
    if (srcMatch?.[1]) return srcMatch[1].trim();

    const bgMatch = markup.match(/url\((?:["'])?([^"')]+)(?:["'])?\)/i);
    if (bgMatch?.[1]) return bgMatch[1].trim();

    return '';
}

function getBannerImageUrl(banner, mobile = false) {
    const directImage = mobile
        ? (getBannerImageValue(banner?.mobilimage) || getBannerImageValue(banner?.image))
        : (getBannerImageValue(banner?.image) || getBannerImageValue(banner?.mobilimage));

    if (directImage) return directImage;

    const markup = mobile
        ? (banner?.mobilcontent || banner?.content || banner?.iframe || '')
        : (banner?.content || banner?.iframe || banner?.mobilcontent || '');

    return extractImageFromMarkup(markup);
}

function getBannerHref(banner, href) {
    const rawUrl = typeof banner?.url === 'string' ? banner.url.trim() : '';

    if (!rawUrl) return "/";

    const normalizeInternalFromAbsolute = (absoluteUrl) => {
        if (!href) return absoluteUrl;

        try {
            const current = new URL(href);
            const target = new URL(absoluteUrl, current.origin);

            if (target.origin === current.origin) {
                return `${target.pathname}${target.search}${target.hash}` || "/";
            }

            return target.toString();
        } catch {
            return absoluteUrl;
        }
    };

    // Preserve absolute/protocol links so they are not mangled by concatenation.
    if (/^https?:\/\//i.test(rawUrl)) {
        return normalizeInternalFromAbsolute(rawUrl);
    }

    if (/^\/\//i.test(rawUrl)) {
        const withProtocol = href?.startsWith('http://') ? `http:${rawUrl}` : `https:${rawUrl}`;
        return normalizeInternalFromAbsolute(withProtocol);
    }

    if (/^(mailto|tel):/i.test(rawUrl)) {
        return rawUrl;
    }

    // Support optional placeholder-based tracking links from backend data.
    if (href && rawUrl.includes('{currentUrl}')) {
        return rawUrl.replaceAll('{currentUrl}', encodeURIComponent(href));
    }

    // Treat non-absolute values as internal paths.
    if (rawUrl.startsWith('/')) {
        return rawUrl;
    }

    return `/${rawUrl.replace(/^\/+/, '')}`;
}

function getProviderTypeMeta(provider, providerTypes = []) {
    const providerTypeId = provider?.type?.id || provider?.provider_type?.id || provider?.provider_category?.id || provider?.main_category?.id || provider?.provider_type_id || provider?.main_category_id;
    const providerTypeUrl = provider?.type?._url || provider?.provider_type?._url || provider?.provider_category?._url || provider?.main_category?._url || provider?.provider_type_url;
    const providerTypeName = provider?.type?.name || provider?.provider_type?.name || provider?.provider_type_name || provider?.provider_category?.name || provider?.main_category?.name || provider?.categories?.[0]?.name || '';

    const matchedType = providerTypes.find((type) => {
        if (!type) return false;
        if (providerTypeId && type.id && +type.id === +providerTypeId) return true;
        if (providerTypeUrl && type._url && type._url === providerTypeUrl) return true;
        if (providerTypeName && type.name && type.name === providerTypeName) return true;
        return false;
    });

    return {
        id: matchedType?.id || providerTypeId || null,
        name: matchedType?.name || providerTypeName || '',
        url: matchedType?._url || providerTypeUrl || ''
    };
}

function matchesProviderType(provider, type, providerTypes = []) {
    if (!type || type === ALL_MOBILE_TYPES_KEY) return true;

    const providerTypeMeta = getProviderTypeMeta(provider, providerTypes);

    if (type.id && providerTypeMeta.id && +type.id === +providerTypeMeta.id) return true;
    if (type._url && providerTypeMeta.url && type._url === providerTypeMeta.url) return true;
    if (type.name && providerTypeMeta.name && type.name === providerTypeMeta.name) return true;

    return false;
}

function getBannerPlaceName(banner) {
    return banner?.provider_name || banner?.name || null;
}

// Safe Banner Component with XSS protection
function SafeBannerContent({ banner, index, sizes = MOSAIC_BANNER_SIZES, mobile = false, preferImage = false }) {
    const bannerMarkup = mobile
        ? (banner.mobilcontent || banner.content || banner.iframe || '')
        : (banner.content || banner.iframe || '');
    const sanitizedBannerMarkup = useSanitizedHTML(bannerMarkup);
    const bannerImage = getBannerImageUrl(banner, mobile);
    const shouldRenderMarkup = hasRenderableBannerMarkup(bannerMarkup);

    if (preferImage && bannerImage) {
        return <img
            src={bannerImage}
            sizes={sizes}
            alt={`banner-${index}`}
            loading="lazy"
            decoding="async"
            style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', inset: 0 }}
        />;
    }

    if (preferImage && !bannerImage) {
        return <SafeImage sizes={sizes} src={EmptyProviderLogo} alt={`banner-${index}`} />;
    }

    if (shouldRenderMarkup) {
        return <div className={`${classes["banner-iframe-container"]}`} dangerouslySetInnerHTML={{__html: sanitizedBannerMarkup}} />;
    }
    
    if (bannerImage) {
        return <img
            src={bannerImage}
            sizes={sizes}
            alt={`banner-${index}`}
            loading="lazy"
            decoding="async"
            style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', inset: 0 }}
        />;
    }

    if (bannerMarkup) {
        return <div className={`${classes["banner-iframe-container"]}`}>{stripHtml(bannerMarkup)}</div>;
    }

    return <SafeImage sizes={sizes} src={EmptyProviderLogo} alt={`banner-${index}`} />;
}

export default function ProvidersMosaicView({banners, providers, provider_types, currentUrl, mobile_tabs = []}) {
    const href = currentUrl || "/";
    const [activeMobileType, setActiveMobileType] = useState(ALL_MOBILE_TYPES_KEY);
    const featuredBanner = banners.find(Boolean) || null;
    const featuredProvider = featuredBanner ? null : providers[0];
    const desktopProviders = providers.slice(1);
    const mobileTabs = (mobile_tabs && mobile_tabs.length > 0 ? mobile_tabs : buildFallbackMobileTabs(providers, provider_types)).filter(Boolean);
    const selectedMobileTab = mobileTabs.find((tab) => tab.key === activeMobileType) || mobileTabs[0] || null;
    const filteredMobileProviders = (selectedMobileTab?.providers || []).filter(Boolean);
    const mobileFeaturedProvider = filteredMobileProviders[0] || null;
    const mobileListProviders = filteredMobileProviders.slice(1);
    const mobileFeedShouldCap = filteredMobileProviders.length > 3;

    useEffect(() => {
        if (!mobileTabs.length) return;
        if (mobileTabs.some((tab) => tab.key === activeMobileType)) return;
        setActiveMobileType(mobileTabs[0].key);
    }, [activeMobileType, mobileTabs]);

    return <div className={`${classes["mosaic-section-main"]}`}>
        <div className="d-flex flex-column align-items-center gap-4 text-center padding-x-8" style={{zIndex: 1}}>
            <h2 className={`margin-0 color-gray-800 display-sm-bold ${classes["mosaic-section-main-title"]}`}>Mindenki aki számít</h2>
            <p className={`margin-0 color-gray-800 display-xs-regular ${classes["mosaic-section-sub-title"]}`}>Találd meg a legfontosabb esküvői szolgáltatókat egy helyen, és válogass a számotokra leginkább megfelelő partnerek közül.</p>
        </div>
        <div className={`${classes["mosaic-grid-container"]}`} style={{gridTemplateColumns: `repeat(${MOSAIC_GRID_COLUMNS}, minmax(0, 1fr))`}}>
            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(1, 21, 1, 8)}>{featuredBanner ? <div className={`${classes["td-inner"]}`}><FeaturedBannerCard banner={featuredBanner} href={href} /></div> : <ProviderCard provider={featuredProvider} sizes={MOSAIC_PROVIDER_CARD_SIZES.width21} />}</div>
            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(22, 11, 1, 14)}><ProviderCard provider={desktopProviders[0]} sizes={MOSAIC_PROVIDER_CARD_SIZES.width11} /></div>
            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(33, 21, 1, 8)}><ProviderCard provider={desktopProviders[1]} sizes={MOSAIC_PROVIDER_CARD_SIZES.width21} /></div>

            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(1, 21, 9, 8)}><ProviderCard provider={desktopProviders[2]} sizes={MOSAIC_PROVIDER_CARD_SIZES.width21} /></div>
            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(33, 21, 9, 8)}><ProviderCard provider={desktopProviders[3]} sizes={MOSAIC_PROVIDER_CARD_SIZES.width21} /></div>

            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(22, 11, 15, 7)}><TypeCard type={provider_types[0]} /></div>

            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(1, 12, 17, 14)}><ProviderCard provider={desktopProviders[4]} sizes={MOSAIC_PROVIDER_CARD_SIZES.width12} /></div>
            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(13, 9, 17, 7)}><TypeCard type={provider_types[1]} /></div>
            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(33, 9, 17, 7)}><TypeCard type={provider_types[2]} /></div>
            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(42, 12, 17, 14)}><ProviderCard provider={desktopProviders[5]} sizes={MOSAIC_PROVIDER_CARD_SIZES.width12} /></div>

            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(22, 11, 22, 7)}><TypeCard type={provider_types[3]} /></div>

            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(13, 9, 24, 7)}><TypeCard type={provider_types[4]} /></div>
            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(33, 9, 24, 7)}><TypeCard type={provider_types[5]} /></div>

            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(22, 11, 29, 7)}><TypeCard type={provider_types[6]} /></div>

            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(1, 21, 31, 8)}><ProviderCard provider={desktopProviders[6]} sizes={MOSAIC_PROVIDER_CARD_SIZES.width21} /></div>
            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(33, 21, 31, 8)}><ProviderCard provider={desktopProviders[7]} sizes={MOSAIC_PROVIDER_CARD_SIZES.width21} /></div>

            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(22, 11, 36, 11)}><ProviderCard provider={desktopProviders[8]} sizes={MOSAIC_PROVIDER_CARD_SIZES.width11} /></div>

            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(1, 21, 39, 8)}><ProviderCard provider={desktopProviders[9]} sizes={MOSAIC_PROVIDER_CARD_SIZES.width21} /></div>
            <div className={`${classes["mosaic-grid-cell"]}`} style={mosaicCell(33, 21, 39, 8)}><ProviderCard provider={desktopProviders[10]} sizes={MOSAIC_PROVIDER_CARD_SIZES.width21} /></div>
        </div>
        <div className={`${classes["mosaic-section-list-view"]}`}>
            {mobileTabs.length > 0 && <div className={`${classes["mosaic-mobile-type-rail"]}`}>
                {mobileTabs.map((tab) => {
                    const typeKey = tab.key;

                    return <button
                        key={`mobile-type-${typeKey}`}
                        type="button"
                        title={tab?.name || "-"}
                        className={`${classes["mosaic-mobile-type-chip"]} ${activeMobileType === typeKey ? classes["mosaic-mobile-type-chip-active"] : ""}`}
                        onClick={() => setActiveMobileType(typeKey)}
                        aria-pressed={activeMobileType === typeKey}
                    >
                        {tab?.name || "-"}
                    </button>;
                })}
            </div>}

            {featuredBanner && <FeaturedBannerCard banner={featuredBanner} href={href} mobile />}

            {mobileFeaturedProvider && <MobileFeaturedProviderCard provider={mobileFeaturedProvider} />}

            {!featuredBanner && !mobileFeaturedProvider && <MobileEmptyState activeType={selectedMobileTab?.type || selectedMobileTab} />}

            <div className={`${classes["mosaic-mobile-feed-shell"]} ${mobileFeedShouldCap ? classes["capped"] : ""}`}>
                <div className={`${classes["mosaic-mobile-feed"]}`}>
                    {mobileListProviders.map((provider, index) => <Fragment key={`mobile-provider-item-${provider.id || index}`}>
                        <MobileProviderFeedCard provider={provider} />
                    </Fragment>)}
                </div>
            </div>

            <div className={`${classes["mosaic-mobile-inline-cta"]}`}>
                <CustomButton size="lg" hierarchy="primary" icon="trailing" is_svg={true} svg={ArrowRight} href={"/szolgaltatok"}>További szolgáltatók</CustomButton>
            </div>
        </div>
        <div className={`${classes["mosaic-section-cta"]}`} style={{zIndex: 1}}><CustomButton size="lg" hierarchy="primary" icon="trailing" is_svg={true} svg={ArrowRight} href={"/szolgaltatok"} >További szolgáltatók</CustomButton></div>
        <div className={`${classes["other-providers-section-background"]}`}>
            <Vectors viewBox={`0 0 ${Vectors().props.width} ${Vectors().props.height}`} />
        </div>
    </div>
}

function MobileFeaturedProviderCard({provider}) {
    if(!provider) return null;

    return <div className={`${classes["mosaic-mobile-featured-card"]}`}>
        <Link title={provider.name || "-"} className={`text-decoration-none pointer ${classes["mosaic-mobile-featured-link"]}`} href={`/${provider.url}`}>
            <div className={`${classes["mosaic-mobile-featured-media"]}`}>
                <img
                    src={customImageLoader({ src: provider.images?.[0] || '', width: 1080, quality: 75 })}
                    srcSet={provider.images?.[0] ? `${customImageLoader({ src: provider.images[0], width: 640, quality: 75 })} 640w, ${customImageLoader({ src: provider.images[0], width: 1080, quality: 75 })} 1080w` : undefined}
                    sizes={MOSAIC_MOBILE_PROVIDER_SIZES}
                    alt={provider.name || "Szolgáltató képe"}
                    loading="lazy"
                    decoding="async"
                    style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', inset: 0 }}
                />
            </div>
            <div className={`${classes["mosaic-mobile-featured-copy"]}`}>
                <h3 className={`${classes["mosaic-mobile-featured-title"]}`}>{provider.name || "-"}</h3>
                <div className={`${classes["mosaic-mobile-location"]}`}>
                    <CustomSVG className={`circle-stroke-white stroke-white flex-shrink-0`} SvgFunction={MapPin} height={18} width={18} />
                    <span>{provider.address?.city || "-"}</span>
                </div>
            </div>
        </Link>
        <div className={`${classes["mosaic-mobile-featured-actions"]}`}>
            <LikeButton providerId={provider.id} initialActive={Boolean(provider.is_favourite)} />
        </div>
    </div>
}

function MobileProviderFeedCard({provider}) {
    const description = stripHtml(provider?.description || provider?.short_description || '');
    const category = provider?.type?.name || provider?.provider_type?.name || provider?.provider_type_name || provider?.provider_category?.name || provider?.categories?.[0]?.name || provider?.main_category?.name || '';

    if(!provider) return null;

    return <div className={`${classes["mosaic-mobile-provider-card"]}`}>
        <Link title={provider.name || "-"} className={`text-decoration-none pointer ${classes["mosaic-mobile-provider-link"]}`} href={`/${provider.url}`}>
            <div className={`${classes["mosaic-mobile-provider-thumb"]}`}>
                <img
                    src={customImageLoader({ src: provider.images?.[0] || '', width: 640, quality: 75 })}
                    srcSet={provider.images?.[0] ? `${customImageLoader({ src: provider.images[0], width: 640, quality: 75 })} 640w, ${customImageLoader({ src: provider.images[0], width: 1080, quality: 75 })} 1080w` : undefined}
                    sizes={MOSAIC_MOBILE_PROVIDER_SIZES}
                    alt={provider.name || "Szolgáltató képe"}
                    loading="lazy"
                    decoding="async"
                    style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', inset: 0 }}
                />
            </div>
            <div className={`${classes["mosaic-mobile-provider-copy"]}`}>
                {category && <div className={`${classes["mosaic-mobile-provider-pill"]}`}>{category}</div>}
                <h3 className={`${classes["mosaic-mobile-provider-title"]}`}>{provider.name || "-"}</h3>
                <div className={`${classes["mosaic-mobile-location"]} ${classes["mosaic-mobile-location-dark"]}`}>
                    <CustomSVG className={`circle-stroke-gray-700 stroke-gray-700 flex-shrink-0`} SvgFunction={MapPin} height={18} width={18} />
                    <span>{provider.address?.city || "-"}</span>
                </div>
                {description && <p className={`${classes["mosaic-mobile-provider-description"]}`}>{description}</p>}
            </div>
        </Link>
        <div className={`${classes["mosaic-mobile-provider-actions"]}`}>
            <LikeButton providerId={provider.id} initialActive={Boolean(provider.is_favourite)} size="sm" />
        </div>
    </div>
}

function MobileEmptyState({activeType}) {
    const title = activeType !== ALL_MOBILE_TYPES_KEY && activeType?.name
        ? `${activeType.name} kategóriában most nincs kiemelt szolgáltató.`
        : 'Most nincs megjeleníthető kiemelt szolgáltató.';

    return <div className={`${classes["mosaic-mobile-empty-state"]}`}>
        <strong>{title}</strong>
        <p>Nézd meg az összes szolgáltatót, vagy válassz másik kategóriát.</p>
    </div>;
}

function FeaturedBannerCard({banner, href, mobile = false}) {
    if (!banner) return null;

    const bannerHref = getBannerHref(banner, href);
    const isInternal = bannerHref.startsWith('/');
    const placeName = getBannerPlaceName(banner);

    return <Link
        className={`${classes["featured-banner-card"]} ${mobile ? classes["featured-banner-card-mobile"] : classes["featured-banner-card-desktop"]}`}
        href={bannerHref}
        prefetch={isInternal}
        title={placeName}
    >
        <div className={`${classes["featured-banner-media"]}`}>
            <SafeBannerContent banner={banner} index={0} sizes={mobile ? MOSAIC_MOBILE_BANNER_SIZES : MOSAIC_BANNER_SIZES} mobile={mobile} preferImage />
        </div>
        <div className={`${classes["featured-banner-chip"]}`}>Kiemelt ajánlat</div>
        {banner.featured_badge && <div className={`${classes["featured-banner-badge"]}`}>
            <span>{banner.featured_badge}</span>
        </div>}
        <div className={`${classes["featured-banner-overlay"]}`}>
            <div className={`${classes["featured-banner-copy"]}`}>
                <h3 className={`${classes["featured-banner-title"]}`}>{placeName}</h3>
                {banner.featured_description && <p className={`${classes["featured-banner-description"]}`}>{banner.featured_description}</p>}
            </div>
            <div className={`${classes["featured-banner-action"]}`}>
                <span>Kapcsolatfelvétel</span>
                <CustomSVG className={`circle-stroke-gray-900 stroke-gray-900 flex-shrink-0`} SvgFunction={ArrowUpRight} height={18} width={18} />
            </div>
        </div>
    </Link>;
}

function TypeCard({type}) {
    const description = stripHtml(type?.description || '');

    return <Link title={(type && type.name) ? type.name : "-"} className={`text-decoration-none pointer ${classes["mosaic-type-link"]}`} href={`/${type["_url"]}`}>
        <div className={`${classes["td-inner"]}`}>
            <div className={`${classes["other-providers-section-type-card"]}`}>
                <h3 className={`${classes["type-card-title"]} text-xl-bold`}>{((type && type.name) ? type.name : "-")}</h3>
                {description && <p className={`${classes["type-card-description"]} text-sm-regular`}>{description}</p>}
            </div>
        </div>
    </Link>
}

function ProviderCard({provider, sizes}) {
    if(!provider) return <div className={`${classes["td-inner"]}`}>
        <div className={`${classes["other-providers-section-provider-card"]}`}>
            <SafeImage sizes={sizes} src={EmptyProviderLogo} alt={"Szolgáltató képe"} />
            <div className={`${classes["provider-card-text"]}`}>
                <h3 className="margin-0 color-white text-start text-xl-bold w-100 text-truncate">-</h3>
                <div className="d-flex flex-row gap-1 align-items-center w-100">
                    <CustomSVG className={`circle-stroke-white stroke-white flex-shrink-0`} SvgFunction={MapPin} height={18} width={18} />
                    <p className="margin-0 text-start color-white text-sm-regular flex-grow-1 text-truncate">-</p>
                </div>
            </div>
        </div>
    </div>

    return <div className={`position-relative w-100 ${classes["mosaic-card-root"]}`}>
        <Link title={(provider && provider.name) ? provider.name : "-"} className={`text-decoration-none pointer ${classes["mosaic-card-link"]}`} href={`/${provider.url}`}>
            <div className={`${classes["td-inner"]}`}>
                <div className={`${classes["other-providers-section-provider-card"]}`}>
                    <img
                        src={customImageLoader({ src: provider.images?.[0] || '', width: 640, quality: 75 })}
                        srcSet={provider.images?.[0] ? `${customImageLoader({ src: provider.images[0], width: 640, quality: 75 })} 640w, ${customImageLoader({ src: provider.images[0], width: 1080, quality: 75 })} 1080w` : undefined}
                        sizes={sizes}
                        alt={provider.name || "Szolgáltató képe"}
                        loading="lazy"
                        decoding="async"
                        style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', inset: 0 }}
                    />
                    <div className={`${classes["provider-card-text"]}`}>
                        <h3 className="margin-0 color-white text-start text-xl-bold w-100 text-truncate">{provider.name}</h3>
                        <div className="d-flex flex-row gap-1 align-items-center w-100">
                            <CustomSVG className={`circle-stroke-white stroke-white flex-shrink-0`} SvgFunction={MapPin} height={18} width={18} />
                            <p className="margin-0 text-start color-white text-sm-regular flex-grow-1 text-truncate">{(provider.address && provider.address.city) ? provider.address.city : "-"}</p>
                        </div>
                    </div>
                </div>
            </div>
        </Link>
        <div className={`${classes["provider-card-button-container"]}`}>
            <LikeButton providerId={provider.id} initialActive={Boolean(provider.is_favourite)} />
            {/*<Link data-tooltip-id={`provider-card-button-view-${provider.id}`} className={`${classes["provider-card-button"]} ${classes["provider-card-button-hide"]}`} title={provider.name} href={`/${provider.url}`}>
                <CustomSVG className={`circle-stroke-white stroke-white flex-shrink-0`} SvgFunction={ArrowUpRight} height={24} width={24} />
            </Link>*/}
            {/*<CustomTooltip id={`provider-card-button-view-${provider.id}`} theme={"dark"} opacity={1} style={{maxWidth: 500}} place_type={"bottom"} >
                <div className='text-xs-semibold'>Profil megtekintés</div>
            </CustomTooltip>*/}
        </div>
    </div>
}

export function LikeButton ({providerId, initialActive = false, size = "lg"}) {
    const authCtx = useContext(AuthContext);
    const normalizedProviderId = Number(providerId);

    useEffect(() => {
        if (!initialActive) return;

        authCtx.setTempFavouriteIds((prev) => (
            prev.some(id => Number(id) === normalizedProviderId) ? prev : [...prev, normalizedProviderId]
        ));
    }, [authCtx, normalizedProviderId, initialActive]);

    const active = authCtx.tempFavouriteIds.some(id => Number(id) === normalizedProviderId);

    return <button className={`${classes["provider-card-button"]} ${size === "sm" ? classes["small"] : ""}`} onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        authCtx.showFavouriteModal(normalizedProviderId);
    }} title="like-button" aria-label="like-button" type="button">
        <CustomSVG className={`${active ? "fill-white" : ""} circle-stroke-white stroke-white flex-shrink-0`} SvgFunction={Heart} height={size === "lg" ? 24 : 16} width={size === "lg" ? 24 : 16} />
    </button>
}